import { assertTextSendOptions } from '../org-chat-delivery';
import type { ClientSurface } from '@/convex/products/ai/lib/client_surface.helpers';
import type { Id } from '@/convex/_generated/dataModel';
import type {
  OrgChatPresetDataInput,
  OrgChatArtifactPanelState,
  OrgChatSelectedSuggestion,
  OrgChatAttachmentInput,
} from '@/components/org-chat/org-chat-types';
import type { FileChipData } from '@/components/tiptap';
import { inferWorkspaceFileMimeType } from '@/components/org-chat/composer-upload-accept';
export type OrgChatApiAttachment = {
  kind: 'image' | 'file';
  filename: string;
  mimeType: string;
  sizeBytes: number;
  storageId?: Id<'_storage'>;
  storageKey?: string;
  url?: string;
  workspaceFileId?: Id<'workspaceFiles'>;
  vfsPath?: string;
  previewUrl?: string;
  uploadId?: string;
  source?: 'upload' | 'selected';
  referenceCategory?: string;
  customReferenceCategoryId?: Id<'customReferenceCategories'>;
};

export type SelectedContextWorkspaceSource = {
  workspaceFileId?: Id<'workspaceFiles'>;
  vfsPath?: string;
};

export function collectSelectedContextWorkspaceSources(
  ...sourceGroups: ReadonlyArray<ReadonlyArray<SelectedContextWorkspaceSource>>
): {
  workspaceFileIds: Array<Id<'workspaceFiles'>>;
  vfsPaths: string[];
} {
  const workspaceFileIds = new Map<string, Id<'workspaceFiles'>>();
  const vfsPaths = new Set<string>();

  for (const source of sourceGroups.flat()) {
    if (source.workspaceFileId) {
      workspaceFileIds.set(
        String(source.workspaceFileId),
        source.workspaceFileId
      );
    }
    const vfsPath = source.vfsPath?.trim();
    if (vfsPath) vfsPaths.add(vfsPath);
  }

  return {
    workspaceFileIds: Array.from(workspaceFileIds.values()),
    vfsPaths: Array.from(vfsPaths),
  };
}

export type OrgChatSendPayload = {
  workspaceNodeReferences?: OrgChatPresetDataInput['workspaceNodeReferences'];
  orgThreadId: Id<'orgThreads'>;
  orgId: Id<'organizations'>;
  prompt: string;
  rawUserPrompt?: string;
  draftContributorUserIds?: Array<Id<'users'>>;
  attachments?: Array<OrgChatApiAttachment>;
  productPresetId?: Id<'globalProductPresets'>;
  productPresetIds?: Array<Id<'globalProductPresets'>>;
  productPresetSubtypeSelections?: OrgChatPresetDataInput['productPresetSubtypeSelections'];
  stylePresetId?: Id<'globalStylePresets'>;
  colorPresetId?: Id<'globalColorPresets'>;
  aestheticId?: Id<'aesthetics'>;
  brandingId?: Id<'brandings'>;
  excludedBrandBlockKeys?: Array<string>;
  composerChips?: OrgChatPresetDataInput['composerChips'];
  selectedColorPresetIds?: Array<Id<'globalColorPresets'>>;
  selectedColorPalette?: Array<string>;
  additionalInstructions?: string;
  selectedUserSkills?: OrgChatPresetDataInput['selectedUserSkills'];
  selectedAvatars?: OrgChatPresetDataInput['selectedAvatars'];
  referenceStorageKeys?: Array<string>;
  referenceStorageIds?: Array<Id<'_storage'>>;
  referenceVfsPaths?: Array<string>;
  selectedContextImageStorageKeys?: Array<string>;
  selectedContextImageStorageIds?: Array<Id<'_storage'>>;
  selectedContextWorkspaceFileIds?: Array<Id<'workspaceFiles'>>;
  selectedContextImageVfsPaths?: Array<string>;
  artifactPanelState?: OrgChatArtifactPanelState;
  /** The sender's device and screen; wording only (client_surface.helpers). */
  clientSurface?: ClientSurface;
  selectedSuggestion?: OrgChatSelectedSuggestion;
  productReferences?: OrgChatPresetDataInput['productReferences'];
  styleReferences?: OrgChatPresetDataInput['styleReferences'];
  colorReferences?: OrgChatPresetDataInput['colorReferences'];
  selectedConnectors?: OrgChatPresetDataInput['selectedConnectors'];
  linkStorageKeysUploaded?: Array<string>;
  linkStorageKeysSelected?: Array<string>;
  linkStorageIdsUploaded?: Array<Id<'_storage'>>;
  linkStorageIdsSelected?: Array<Id<'_storage'>>;
  forceDeepResearch?: boolean;
  fixedModelAlias?: OrgChatPresetDataInput['fixedModelAlias'];
  voiceMode?: boolean;
  realtimeVoiceSessionId?: string;
  /** Set only by the composer's own submit; see `isHumanTypedSend`. */
  typedByUser?: true;
  lastAssistantText?: string;
  audioBase64?: string;
  deliveryMetadata?: {
    deliveryKind: 'send' | 'queue' | 'steer';
    clientInputId?: string;
    steerId?: string;
    targetPromptMessageId?: string;
    targetWorkflowId?: string;
    createdAt?: number;
  };
};

export function sanitizeOrgChatSendPayloadForTransport(
  payload: OrgChatSendPayload
): OrgChatSendPayload {
  assertTextSendOptions(payload);
  if (
    !payload.selectedSuggestion ||
    !('isPrimary' in payload.selectedSuggestion)
  ) {
    return payload;
  }
  const selectedSuggestion = { ...payload.selectedSuggestion };
  delete selectedSuggestion.isPrimary;
  return { ...payload, selectedSuggestion };
}

export const USER_SKILL_CHIP_UPLOAD_ID_PREFIX = 'user-skill:';

export const CUSTOM_REFERENCE_UPLOAD_ID_PREFIX = 'custom-reference:';

export function parseCustomReferenceUploadId(uploadId: string): {
  categoryId: Id<'customReferenceCategories'>;
  vfsPath: string;
} | null {
  if (!uploadId.startsWith(CUSTOM_REFERENCE_UPLOAD_ID_PREFIX)) return null;
  const remainder = uploadId.slice(CUSTOM_REFERENCE_UPLOAD_ID_PREFIX.length);
  const separatorIndex = remainder.indexOf(':');
  if (separatorIndex <= 0) return null;
  const categoryId = remainder.slice(0, separatorIndex);
  const vfsPath = remainder.slice(separatorIndex + 1);
  if (!categoryId || !vfsPath) return null;
  return {
    categoryId: categoryId as Id<'customReferenceCategories'>,
    vfsPath,
  };
}

export function isCustomReferenceAttachment(
  attachment: Pick<
    OrgChatAttachmentInput,
    'referenceCategory' | 'customReferenceCategoryId' | 'uploadId'
  >
): boolean {
  return (
    attachment.referenceCategory === 'custom-reference' ||
    Boolean(attachment.customReferenceCategoryId) ||
    Boolean(parseCustomReferenceUploadId(attachment.uploadId ?? ''))
  );
}

export function customReferenceFieldsFromUploadId(uploadId: string): {
  referenceCategory: 'custom-reference';
  customReferenceCategoryId: Id<'customReferenceCategories'>;
  vfsPath?: string;
} | null {
  const parsed = parseCustomReferenceUploadId(uploadId);
  if (!parsed) return null;
  return {
    referenceCategory: 'custom-reference',
    customReferenceCategoryId: parsed.categoryId,
    vfsPath: parsed.vfsPath,
  };
}

export function applyCustomReferenceFields(
  attachment: OrgChatAttachmentInput,
  uploadId: string
): OrgChatAttachmentInput {
  const customReferenceFields = customReferenceFieldsFromUploadId(uploadId);
  if (!customReferenceFields) return attachment;
  return {
    ...attachment,
    ...customReferenceFields,
    vfsPath: attachment.vfsPath ?? customReferenceFields.vfsPath,
  };
}

export const COMPOSER_UPLOAD_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isComposerUploadChip(chip: FileChipData): boolean {
  return (
    chip.status === 'complete' &&
    Boolean(chip.storageKey?.trim()) &&
    (COMPOSER_UPLOAD_UUID_RE.test(chip.uploadId) ||
      chip.uploadId.startsWith('upload-'))
  );
}

export function isSelectedImageChip(chip: FileChipData): boolean {
  const mimeType = inferWorkspaceFileMimeType({
    path: chip.filename,
    mimeType: chip.mimeType,
  });
  return (
    chip.status === 'complete' &&
    mimeType.startsWith('image/') &&
    !chip.uploadId.startsWith(USER_SKILL_CHIP_UPLOAD_ID_PREFIX) &&
    !chip.uploadId.startsWith(CUSTOM_REFERENCE_UPLOAD_ID_PREFIX) &&
    !chip.uploadId.startsWith('preset:') &&
    Boolean(
      chip.storageKey?.trim() ||
        chip.storageId ||
        chip.vfsPath?.trim() ||
        chip.url?.trim() ||
        chip.previewUrl?.trim()
    )
  );
}

export function isCustomReferenceChip(chip: FileChipData): boolean {
  return (
    chip.status === 'complete' &&
    Boolean(parseCustomReferenceUploadId(chip.uploadId))
  );
}

export function isSelectedWorkspaceFileChip(chip: FileChipData): boolean {
  return (
    chip.status === 'complete' &&
    chip.semanticType !== 'skill' &&
    !chip.uploadId.startsWith(USER_SKILL_CHIP_UPLOAD_ID_PREFIX) &&
    !chip.uploadId.startsWith(CUSTOM_REFERENCE_UPLOAD_ID_PREFIX) &&
    !chip.uploadId.startsWith('preset:') &&
    Boolean(chip.workspaceFileId || chip.vfsPath?.trim())
  );
}

/**
 * A complete reference chip that names a stored file directly (no workspace
 * path), such as a storage-ID-only PDF restored from a pasted message.
 */
export function isSelectedStoredFileChip(chip: FileChipData): boolean {
  return (
    chip.status === 'complete' &&
    chip.semanticType !== 'skill' &&
    !chip.uploadId.startsWith(USER_SKILL_CHIP_UPLOAD_ID_PREFIX) &&
    !chip.uploadId.startsWith(CUSTOM_REFERENCE_UPLOAD_ID_PREFIX) &&
    !chip.uploadId.startsWith('preset:') &&
    Boolean(chip.storageId?.trim() || chip.storageKey?.trim())
  );
}
